import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, Building2, CalendarDays, MapPin, UserCheck, Loader2,
  Download, AlertTriangle, ArrowRightLeft, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StandardReportTab = ({ token, filters, filterOptions, isDarkMode }) => {
  const [reportConfig, setReportConfig] = useState({
    tag: filters.tag || '',
    client_name: '',
    cnpj: '',
    exercise_start_date: new Date().toISOString().split('T')[0],
    total_employees: '',
    location: '',
    responsible_name: 'Douglas Viana de Moraes',
    responsible_title: 'Ergonomista Eng. Seg. Trab.',
    responsible_registration: 'CREA/RJ 2022101176'
  });
  const [generating, setGenerating] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [analysisPreview, setAnalysisPreview] = useState(null);
  const [protectionTexts, setProtectionTexts] = useState({});
  const [riskTexts, setRiskTexts] = useState({});
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [periodYear, setPeriodYear] = useState('');
  const [periodRound, setPeriodRound] = useState('');
  const [comparisonYear, setComparisonYear] = useState('');
  const [comparisonRound, setComparisonRound] = useState('');
  const [enableComparison, setEnableComparison] = useState(false);
  const [showCompanyDetails, setShowCompanyDetails] = useState(true);
  const [showResponsible, setShowResponsible] = useState(false);

  const fetchAvailableTags = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/tag-status`);
      if (response.data.success) setAvailableTags(response.data.tags || []);
    } catch (error) { console.error('Error fetching tags:', error); }
  }, [token]);

  useEffect(() => { fetchAvailableTags(); }, [fetchAvailableTags]);

  useEffect(() => {
    if (filters.tag) {
      setReportConfig(prev => (prev.tag === filters.tag ? prev : { ...prev, tag: filters.tag }));
    }
    // Intencionalmente exclui reportConfig.tag das deps para não reverter seleção manual do dropdown
  }, [filters.tag]);

  const fetchPeriods = async (tagValue) => {
    try {
      const response = await axios.get(`${API}/admin/available-periods?tag=${encodeURIComponent(tagValue)}`);
      if (response.data.success) {
        const periods = response.data.periods || [];
        setAvailablePeriods(periods);
        if (periods.length > 0) { setPeriodYear(periods[0].year); setPeriodRound(periods[0].round || 1); }
        if (periods.length > 1) { setComparisonYear(periods[1].year); setComparisonRound(periods[1].round || 1); }
        else { setComparisonYear(''); setComparisonRound(''); }
      }
    } catch (error) { console.error('Error fetching periods:', error); }
  };

  const handleTagSelect = async (tagValue) => {
    const selectedTag = availableTags.find(t => t.tag === tagValue);
    const fallbackName = tagValue.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const lastConfig = selectedTag?.last_emission_config || null;
    const newClientName = lastConfig?.client_name || selectedTag?.client_name || fallbackName;
    setReportConfig(prev => ({
      ...prev,
      tag: tagValue,
      client_name: newClientName,
      total_employees: lastConfig?.total_employees || selectedTag?.employee_count || '',
      // Pré-preenche com os dados da última emissão desta empresa (se houver)
      cnpj: lastConfig?.cnpj || '',
      location: lastConfig?.location || '',
      responsible_name: lastConfig?.responsible_name || prev.responsible_name,
      responsible_title: lastConfig?.responsible_title || prev.responsible_title,
      responsible_registration: lastConfig?.responsible_registration || prev.responsible_registration
    }));
    setReportResult(null); setAnalysisPreview(null); setProtectionTexts({}); setRiskTexts({}); setEnableComparison(false);

    if (tagValue) {
      fetchPeriods(tagValue);
      setLoadingPreview(true);
      try {
        const response = await axios.post(`${API}/admin/preview-report-analysis`, { tag: tagValue, client_name: newClientName });
        if (response.data.success) {
          setAnalysisPreview(response.data);
          setProtectionTexts(response.data.protection_texts || {});
          setRiskTexts(response.data.risk_texts || {});
        }
      } catch (error) { console.error('Error loading analysis preview:', error); }
      setLoadingPreview(false);
    }
  };

  const generateStandardReport = async () => {
    if (!reportConfig.tag) { alert('Selecione a tag da empresa para gerar o relatório'); return; }
    setGenerating(true); setReportResult(null);
    try {
      const response = await axios.post(`${API}/admin/generate-standard-report`, {
        tag: reportConfig.tag, client_name: reportConfig.client_name, cnpj: reportConfig.cnpj,
        exercise_start_date: reportConfig.exercise_start_date,
        total_employees: reportConfig.total_employees ? parseInt(reportConfig.total_employees) : 0,
        location: reportConfig.location, responsible_name: reportConfig.responsible_name,
        responsible_title: reportConfig.responsible_title, responsible_registration: reportConfig.responsible_registration,
        custom_protection_texts: protectionTexts, custom_risk_texts: riskTexts,
        period_year: periodYear, period_round: periodRound,
        comparison_year: enableComparison ? comparisonYear : '', comparison_round: enableComparison ? comparisonRound : ''
      });
      if (response.data.success) {
        setReportResult(response.data);
        const link = document.createElement('a');
        link.href = `data:${response.data.mime_type};base64,${response.data.data}`;
        link.download = response.data.filename;
        link.click();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao gerar relatório padronizado';
      alert(msg);
    }
    setGenerating(false);
  };

  const inputClass = `w-full text-sm border rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-demoraes-green/30 focus:border-demoraes-green transition-colors ${
    isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-200 placeholder-zinc-500' : 'bg-white border-zinc-300 text-zinc-700 placeholder-zinc-400'
  }`;
  const labelClass = `text-xs font-semibold uppercase tracking-wider mb-1.5 block ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`;

  const SectionToggle = ({ label, icon: Icon, open, onToggle }) => (
    <button onClick={onToggle}
      className={`w-full flex items-center justify-between py-3 text-left ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
      <span className="flex items-center gap-2 text-sm font-semibold">
        <Icon size={15} className="text-demoraes-green" /> {label}
      </span>
      {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={`rounded-lg border p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <div className="flex items-center gap-2 mb-1">
          <FileText size={18} className="text-demoraes-green" />
          <h3 className={`text-base font-bold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            Relatório Padronizado
          </h3>
        </div>
        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
          Documento DOCX com análise completa, classificação IPTO e plano de ação
        </p>
      </div>

      {/* Tag Selection */}
      <div className={`rounded-lg border p-5 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <label className={labelClass}>Empresa / Tag *</label>
        <select value={reportConfig.tag} onChange={(e) => handleTagSelect(e.target.value)}
          className={inputClass} data-testid="report-tag-select">
          <option value="">Selecione uma empresa...</option>
          {availableTags.map(tag => (
            <option key={tag.tag} value={tag.tag}>
              {tag.client_name} ({tag.tag}) — {tag.current_responses}/{tag.employee_count}{tag.report_emitted ? ' • ✓ relatório emitido' : ''}
            </option>
          ))}
          {filterOptions.tags && filterOptions.tags.filter(t => !availableTags.find(at => at.tag === t)).map(tag => (
            <option key={tag} value={tag}>{tag}</option>
          ))}
        </select>
        {(() => {
          const sel = availableTags.find(t => t.tag === reportConfig.tag);
          if (!sel) return null;
          const fmtDateTime = (iso) => {
            const d = new Date(iso);
            if (isNaN(d)) return null;
            return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
          };
          const fmtDelta = (fromIso, toIso) => {
            const ms = new Date(toIso) - new Date(fromIso);
            if (isNaN(ms) || ms < 0) return null;
            const days = Math.floor(ms / 86400000);
            const hours = Math.floor((ms % 86400000) / 3600000);
            const mins = Math.floor((ms % 3600000) / 60000);
            const parts = [];
            if (days) parts.push(`${days}d`);
            if (hours) parts.push(`${hours}h`);
            parts.push(`${mins}min`);
            return parts.join(' ');
          };
          const emissionDelta = sel.last_emission_at && sel.last_response_at
            ? fmtDelta(sel.last_response_at, sel.last_emission_at)
            : null;
          return (
            <div className="mt-2 space-y-1.5">
              {sel.last_response_at && (
                <div
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md ${
                    isDarkMode ? 'bg-zinc-800/60 text-zinc-400' : 'bg-zinc-50 text-zinc-600'
                  }`}
                  data-testid="last-response-notice"
                >
                  <CalendarDays size={12} />
                  <span>
                    Última resposta recebida em {fmtDateTime(sel.last_response_at)}
                    {' '}(acompanhamento interno — esta informação não consta no relatório).
                  </span>
                </div>
              )}
              {sel.report_emitted && (
                <div
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md ${
                    isDarkMode ? 'bg-sky-900/20 text-sky-400' : 'bg-sky-50 text-sky-700'
                  }`}
                  data-testid="report-emitted-notice"
                >
                  <FileText size={12} />
                  <span>
                    Esta empresa já possui relatório emitido
                    {sel.last_emission_at
                      ? ` (último em ${new Date(sel.last_emission_at).toLocaleDateString('pt-BR')})`
                      : ''}
                    {emissionDelta
                      ? ` — emitido ${emissionDelta} após a última resposta`
                      : ''}. Gerar novamente irá produzir uma nova emissão.
                  </span>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Period Selection */}
      {availablePeriods.length > 0 && (
        <div className={`rounded-lg border p-5 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}
          data-testid="period-section">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Período Principal</label>
              <select
                value={periodYear && periodRound ? `${periodYear}_R${periodRound}` : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) { setPeriodYear(''); setPeriodRound(''); }
                  else { const p = availablePeriods.find(p => `${p.year}_R${p.round}` === val); if (p) { setPeriodYear(p.year); setPeriodRound(p.round); } }
                  setReportResult(null);
                }}
                className={inputClass} data-testid="period-year-select">
                <option value="">Todos os períodos</option>
                {availablePeriods.map(p => (
                  <option key={`${p.year}_R${p.round}`} value={`${p.year}_R${p.round}`}>
                    {p.label} ({p.count} respostas)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer mb-1.5">
                <input type="checkbox" checked={enableComparison} onChange={(e) => setEnableComparison(e.target.checked)}
                  className="rounded border-zinc-400 w-3.5 h-3.5 text-demoraes-green focus:ring-demoraes-green"
                  data-testid="enable-comparison-checkbox" />
                <span className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                  Comparar com período anterior
                </span>
              </label>
              {enableComparison && (
                <select
                  value={comparisonYear && comparisonRound ? `${comparisonYear}_R${comparisonRound}` : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) { setComparisonYear(''); setComparisonRound(''); }
                    else { const p = availablePeriods.find(p => `${p.year}_R${p.round}` === val); if (p) { setComparisonYear(p.year); setComparisonRound(p.round); } }
                    setReportResult(null);
                  }}
                  className={inputClass} data-testid="comparison-year-select">
                  <option value="">Selecione...</option>
                  {availablePeriods.filter(p => `${p.year}_R${p.round}` !== `${periodYear}_R${periodRound}`).map(p => (
                    <option key={`${p.year}_R${p.round}`} value={`${p.year}_R${p.round}`}>
                      {p.label} ({p.count} respostas)
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          {enableComparison && comparisonYear && periodYear && (
            <div className={`flex items-center gap-2 mt-3 p-2 rounded-md text-xs ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-50 text-zinc-600'}`}>
              <ArrowRightLeft size={13} className="text-demoraes-green" />
              Seção comparativa: {comparisonYear} R{comparisonRound} vs {periodYear} R{periodRound}
            </div>
          )}
        </div>
      )}

      {/* Company Details (collapsible) */}
      <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <div className="px-5">
          <SectionToggle label="Dados da Empresa" icon={Building2} open={showCompanyDetails} onToggle={() => setShowCompanyDetails(!showCompanyDetails)} />
        </div>
        {showCompanyDetails && (
          <div className={`px-5 pb-5 border-t ${isDarkMode ? 'border-zinc-800' : 'border-zinc-100'}`}>
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <div>
                <label className={labelClass}>Nome da Empresa</label>
                <input type="text" value={reportConfig.client_name}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, client_name: e.target.value }))}
                  placeholder="Ex: Serraria Melodia" className={inputClass} data-testid="report-client-name" />
              </div>
              <div>
                <label className={labelClass}>CNPJ</label>
                <input type="text" value={reportConfig.cnpj}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, cnpj: e.target.value }))}
                  placeholder="00.000.000/0001-00" className={inputClass} data-testid="report-cnpj" />
              </div>
              <div>
                <label className={labelClass}>Data de Elaboração</label>
                <input type="date" value={reportConfig.exercise_start_date}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, exercise_start_date: e.target.value }))}
                  className={inputClass} data-testid="report-exercise-date" />
                {reportConfig.exercise_start_date && (
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>
                    Vigência 12 meses: até {(() => { const d = new Date(reportConfig.exercise_start_date + 'T12:00:00'); d.setFullYear(d.getFullYear(), d.getMonth() + 12); return d.toLocaleDateString('pt-BR'); })()}
                  </p>
                )}
              </div>
              <div>
                <label className={labelClass}>Total de Funcionários</label>
                <input type="number" min="1" value={reportConfig.total_employees}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, total_employees: e.target.value }))}
                  placeholder="Total registrados" className={inputClass} data-testid="report-total-employees" />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Localidade</label>
                <input type="text" value={reportConfig.location}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Ex: Nova Friburgo - RJ" className={inputClass} data-testid="report-location" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Responsible Professional (collapsible) */}
      <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <div className="px-5">
          <SectionToggle label="Responsável Técnico" icon={UserCheck} open={showResponsible} onToggle={() => setShowResponsible(!showResponsible)} />
        </div>
        {showResponsible && (
          <div className={`px-5 pb-5 border-t ${isDarkMode ? 'border-zinc-800' : 'border-zinc-100'}`}>
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <div>
                <label className={labelClass}>Nome</label>
                <input type="text" value={reportConfig.responsible_name}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, responsible_name: e.target.value }))}
                  className={inputClass} data-testid="report-responsible-name" />
              </div>
              <div>
                <label className={labelClass}>Título/Cargo</label>
                <input type="text" value={reportConfig.responsible_title}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, responsible_title: e.target.value }))}
                  className={inputClass} data-testid="report-responsible-title" />
              </div>
              <div>
                <label className={labelClass}>Registro</label>
                <input type="text" value={reportConfig.responsible_registration}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, responsible_registration: e.target.value }))}
                  className={inputClass} data-testid="report-responsible-registration" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Qualitative Analysis */}
      {loadingPreview && (
        <div className={`rounded-lg border p-8 flex items-center justify-center ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
          <Loader2 size={20} className="animate-spin text-demoraes-green mr-3" />
          <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>Carregando análise qualitativa...</span>
        </div>
      )}

      {analysisPreview && (
        <div className={`rounded-lg border p-5 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}
          data-testid="analysis-preview-section">
          <h4 className={`text-sm font-bold mb-1 ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>Análise Qualitativa</h4>
          <p className={`text-xs mb-4 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Textos gerados automaticamente. Edite antes de gerar o relatório.
          </p>

          {Object.keys(protectionTexts).length > 0 && (
            <div className="mb-4">
              <h5 className="text-xs font-semibold uppercase tracking-wider text-demoraes-green mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-demoraes-green" /> Fatores de Proteção (pontuação ≥ 75 ou 0% expostos)
              </h5>
              {Object.entries(protectionTexts).map(([dimKey, text]) => {
                const info = analysisPreview.dimensions_info[dimKey];
                return (
                  <div key={dimKey} className="mb-2">
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      {info?.label} ({info?.prevalence ? `${Number(info?.average_score ?? 0).toFixed(1)}% expostos` : `${Number(info?.average_score ?? 0).toFixed(1)}/100`})
                    </label>
                    <textarea value={text} onChange={(e) => setProtectionTexts(prev => ({ ...prev, [dimKey]: e.target.value }))}
                      rows={2}
                      className={`w-full text-sm border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-demoraes-green/30 ${
                        isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-200' : 'bg-white border-demoraes-green/20 text-zinc-700'
                      }`}
                      data-testid={`protection-text-${dimKey}`} />
                  </div>
                );
              })}
            </div>
          )}

          {Object.keys(riskTexts).length > 0 && (
            <div>
              <h5 className="text-xs font-semibold uppercase tracking-wider text-amber-500 mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Fatores de Risco (pontuação &lt; 75 ou com exposição registrada)
              </h5>
              {Object.entries(riskTexts).map(([dimKey, text]) => {
                const info = analysisPreview.dimensions_info[dimKey];
                return (
                  <div key={dimKey} className="mb-2">
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      {info?.label} ({info?.prevalence ? `${Number(info?.average_score ?? 0).toFixed(1)}% expostos` : `${Number(info?.average_score ?? 0).toFixed(1)}/100`} — {info?.risk_level})
                    </label>
                    <textarea value={text} onChange={(e) => setRiskTexts(prev => ({ ...prev, [dimKey]: e.target.value }))}
                      rows={2}
                      className={`w-full text-sm border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/30 ${
                        isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-200' : 'bg-white border-amber-200 text-zinc-700'
                      }`}
                      data-testid={`risk-text-${dimKey}`} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Generate Button */}
      <button onClick={generateStandardReport} disabled={generating || !reportConfig.tag}
        className="w-full bg-demoraes-green hover:bg-demoraes-green/90 text-white font-bold py-3 rounded-md transition-all disabled:opacity-40 text-sm"
        data-testid="generate-standard-report-btn">
        {generating ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Gerando Relatório...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <FileText size={16} /> Gerar Relatório Padronizado
          </span>
        )}
      </button>

      {/* Result */}
      {reportResult && (
        <div className={`rounded-lg border p-5 ${isDarkMode ? 'bg-zinc-900 border-demoraes-green/30' : 'bg-white border-demoraes-green/30'}`}
          data-testid="report-result">
          <h4 className="text-sm font-bold text-demoraes-green mb-3 flex items-center gap-2">
            <Download size={15} /> Relatório Gerado
          </h4>
          <div className={`grid grid-cols-2 gap-2 text-xs mb-3 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
            <div><strong className={isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}>Empresa:</strong> {reportResult.client_name}</div>
            <div><strong className={isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}>IPTO 2.0:</strong> {reportResult.ipto_score}/100</div>
            <div><strong className={isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}>Risco:</strong> {reportResult.risk_level}</div>
            <div><strong className={isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}>Respondentes:</strong> {reportResult.total_respondents}/{reportResult.total_employees}</div>
          </div>
          {reportResult.harassment_detected && (
            <div className="mb-3 p-2.5 rounded-md bg-red-600 text-white text-xs font-semibold flex items-center gap-2"
              data-testid="harassment-alert">
              <AlertTriangle size={14} /> Situação de assédio/violência identificada. Plano de ação imediato incluído.
            </div>
          )}
          {reportResult.has_comparison && (
            <div className={`mb-3 p-2.5 rounded-md text-xs font-semibold flex items-center gap-2 ${
              isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-50 text-zinc-700'
            }`} data-testid="comparison-info">
              <ArrowRightLeft size={13} className="text-demoraes-green" />
              Comparativo: {reportResult.comparison_label || reportResult.comparison_year} vs {reportResult.period_label || reportResult.period_year}
            </div>
          )}
          <button onClick={() => {
            const link = document.createElement('a');
            link.href = `data:${reportResult.mime_type};base64,${reportResult.data}`;
            link.download = reportResult.filename;
            link.click();
          }}
            className="bg-demoraes-green hover:bg-demoraes-green/90 text-white text-xs font-semibold py-2 px-4 rounded-md transition-all inline-flex items-center gap-1.5"
            data-testid="download-report-btn">
            <Download size={13} /> Baixar DOCX Novamente
          </button>
        </div>
      )}
    </div>
  );
};
